import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { calcularLineaPesoManual, formatMonedaPeso } from '../../utils/pesoManualVenta';
import { CATEGORIA_PESO_LABELS, isCodigoInterno } from '../../utils/codigoInterno';

const formatMoneda = (value) => formatMonedaPeso(value);

const fieldClass =
  'nexo-field w-full rounded-xl border-2 border-slate-200 bg-white text-slate-900 ' +
  'focus:border-e0e3-accent focus:outline-none focus:ring-4 focus:ring-e0e3-accent/25';

export default function PesoManualVentaModal({
  isOpen,
  onClose,
  producto,
  posPoliticas = {},
  onAgregar
}) {
  const pesoRef = useRef(null);
  const [pesoKg, setPesoKg] = useState('');
  const [importeManual, setImporteManual] = useState('');
  const [modoImporteManual, setModoImporteManual] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  const precioPorKg = Number(producto?.precioPorKg ?? producto?.precio_por_kg ?? producto?.precio_venta ?? 0);
  const redondeoStep = Number(posPoliticas.redondeo_peso_manual ?? 0);

  const calculo = useMemo(() => {
    if (!pesoKg && !modoImporteManual) return null;
    return calcularLineaPesoManual({
      pesoKg: modoImporteManual ? 1 : pesoKg,
      precioPorKg: precioPorKg,
      redondeoStep: modoImporteManual ? 0 : redondeoStep,
      importeManual: modoImporteManual ? importeManual : undefined
    });
  }, [pesoKg, importeManual, modoImporteManual, precioPorKg, redondeoStep]);

  useEffect(() => {
    if (!isOpen) return;
    setPesoKg('');
    setImporteManual('');
    setModoImporteManual(false);
    setAdminEmail('');
    setAdminPassword('');
    setTimeout(() => pesoRef.current?.focus(), 120);
  }, [isOpen, producto?.id]);

  const confirmar = () => {
    if (!posPoliticas.permitir_venta_peso_manual) {
      toast.error('La empresa no permite venta por peso manual');
      return;
    }
    let linea;
    if (modoImporteManual) {
      const imp = parseFloat(String(importeManual).replace(',', '.'));
      if (Number.isNaN(imp) || imp <= 0) {
        toast.error('Ingresá un importe final válido');
        return;
      }
      if (posPoliticas.requiere_autorizacion_modificar_importe_peso && (!adminEmail.trim() || !adminPassword)) {
        toast.warning('Se requieren credenciales de administrador');
        return;
      }
      linea = {
        valid: true,
        pesoKg: Number(pesoKg) > 0 ? Number(pesoKg) : 1,
        precioPorKg,
        subtotalCalculado: imp,
        subtotalFinal: imp,
        redondeoAplicado: 0,
        importeManualAutorizado: true,
        adminEmail: adminEmail.trim(),
        adminPassword
      };
    } else {
      linea = calcularLineaPesoManual({ pesoKg, precioPorKg, redondeoStep });
    }
    if (!linea?.valid) {
      toast.error(linea?.error || 'Datos inválidos');
      return;
    }
    onAgregar?.({
      ...linea,
      producto,
      adminEmail: linea.adminEmail,
      adminPassword: linea.adminPassword
    });
    onClose?.();
  };

  if (!producto) return null;

  const codigo = producto.codigo || producto.codigoManual || producto.codigo_barras || '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Venta por peso" size="md">
      <div className="space-y-3" data-peso-manual-modal>
        <div className="rounded-lg bg-slate-50 p-3 text-sm">
          <div><span className="text-slate-500">Código:</span> <strong>{codigo}</strong></div>
          {isCodigoInterno(codigo) && (
            <span className="mt-1 inline-block rounded bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-900">
              Código interno
            </span>
          )}
          <div className="mt-1 font-semibold">{producto.nombre}</div>
          {producto.categoriaPeso && (
            <div className="text-xs text-slate-500">
              {CATEGORIA_PESO_LABELS[producto.categoriaPeso] || producto.categoriaPeso}
            </div>
          )}
          <div className="mt-1">Precio Kg: <strong>{formatMoneda(precioPorKg)}</strong></div>
        </div>

        {!modoImporteManual && (
          <div>
            <label className="text-sm font-semibold text-slate-700">Peso (kg)</label>
            <input
              ref={pesoRef}
              type="number"
              min="0"
              step="0.001"
              className={fieldClass}
              value={pesoKg}
              onChange={(e) => setPesoKg(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && confirmar()}
            />
          </div>
        )}

        {calculo?.valid && !modoImporteManual && (
          <div className="rounded-lg border border-slate-200 p-3 text-sm">
            <div>Importe: <strong>{formatMoneda(calculo.subtotalCalculado)}</strong></div>
            <div>Redondeado: <strong className="text-emerald-700">{formatMoneda(calculo.subtotalFinal)}</strong></div>
          </div>
        )}

        {posPoliticas.permitir_modificar_importe_peso_manual && (
          <>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={modoImporteManual}
                onChange={(e) => setModoImporteManual(e.target.checked)}
              />
              Precio final manual (ej. $5000 de carne picada)
            </label>
            {modoImporteManual && (
              <>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Precio final</label>
                  <input
                    type="number"
                    min="0"
                    className={fieldClass}
                    value={importeManual}
                    onChange={(e) => setImporteManual(e.target.value)}
                  />
                </div>
                {posPoliticas.requiere_autorizacion_modificar_importe_peso && (
                  <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs font-semibold text-amber-900">Autorización administrador</p>
                    <input
                      className={fieldClass}
                      placeholder="Email admin"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                    />
                    <input
                      type="password"
                      className={fieldClass}
                      placeholder="Contraseña admin"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}

        <Button color="primary" className="min-h-[48px] w-full" onClick={confirmar}>
          Agregar al carrito
        </Button>
      </div>
    </Modal>
  );
}
